import React, { Component } from "react";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Button from "@amzn/meridian/button";
import Alert from "@amzn/meridian/alert";
import Text from "@amzn/meridian/text";
import { ROUTES } from "@/app/app-constants";
import rumService from "@/utils/awsRUM/awsRUM";

interface Props {
  /** Human-readable name shown in the error UI (e.g. "Owner Configuration"). */
  appName: string;
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error boundary for an extracted child app. Catches render-time crashes in
 * the child WITHOUT affecting the rest of the route area (other routes keep
 * working; the app shell stays visible).
 *
 * Shows a friendly message + retry/navigate-away instead of the shared
 * RouteErrorBoundary's generic error screen.
 */
export class ChildAppErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    rumService.customEvent({
      type: "Error",
      message: `ChildApp crash: ${this.props.appName}`,
      data: { error: error.message, stack: info.componentStack?.slice(0, 500) },
    });
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): React.ReactNode {
    if (!this.state.hasError) return this.props.children;

    return (
      <Column height="100%" alignmentHorizontal="center" alignmentVertical="center" spacing="400">
        <Alert type="error" size="large">
          {this.props.appName} failed to load.
        </Alert>
        <Text>
          This section encountered an error. Other parts of the application are unaffected.
        </Text>
        <Row spacing="300">
          <Button onClick={this.handleRetry}>Try again</Button>
          <Button
            type="tertiary"
            onClick={() => {
              window.location.href = ROUTES.RYC;
            }}
          >
            Go to Yard Control
          </Button>
        </Row>
      </Column>
    );
  }
}
